/**
 * OwnshipController — effect-only child component for the GPS ownship
 * marker + follow-ownship pan behavior.
 *
 * Extracted from MapView.tsx as Phase 5 of the HI-2' refactor
 * (audit synthesis 2026-05-24). Owns:
 *   - Ownship marker creation / positioning / rotation
 *   - Active aircraft marker style (arrow vs custom SVG)
 *   - Follow-ownship pan (easeTo on GPS fix)
 *   - __fyProgrammingMove flag (L2 audit item — moved from (map as any) to
 *     a proper ref scoped to this controller)
 *
 * Returns null — no DOM output.
 */

import { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';
import { useGpsStore } from '../../stores/gpsStore';
import { useAircraftStore } from '../../stores/aircraftStore';
import { usePlanStore } from '../../stores/planStore';
import { ownshipMarkerSvg } from '../ownshipMarkerSvg';
import { getMapInstance } from '../MapView';

export function OwnshipController() {
  const showOwnship  = useLayerVisibilityStore((s) => s.layers.ownship);
  const followOwnship = useUiStore((s) => s.followOwnship);

  // Active aircraft marker style — driven by the plan's selected
  // aircraft, fallback to default aircraft, fallback to 'arrow'.
  const planAircraftId = usePlanStore((s) => s.aircraftId);
  const aircraftList   = useAircraftStore((s) => s.list);
  const activeOwnshipMarker = useMemo(() => {
    const byPlan = aircraftList.find((a) => a.id === planAircraftId);
    if (byPlan?.ownshipMarker) return byPlan.ownshipMarker;
    const byDefault = aircraftList.find((a) => a.isDefault) ?? aircraftList[0];
    return byDefault?.ownshipMarker ?? 'arrow';
  }, [aircraftList, planAircraftId]);

  // Stable ref to the current MapLibre Marker instance.
  const ownshipMarker = useRef<maplibregl.Marker | null>(null);

  // Ownship marker placement + rotation.
  // Subscribes directly to GPS store so this effect runs on every fix
  // without an intermediate React re-render — keeps 60 fps heading
  // updates smooth even on slow iPhones.
  useEffect(() => {
    const renderMarker = (state: ReturnType<typeof useGpsStore.getState>) => {
      const map = getMapInstance();
      if (!map) return;
      const fix = state.fix;
      if (!fix || !showOwnship) {
        ownshipMarker.current?.remove();
        ownshipMarker.current = null;
        return;
      }
      if (!ownshipMarker.current) {
        const el = document.createElement('div');
        el.className = 'ownship-marker';
        el.innerHTML = ownshipMarkerSvg(activeOwnshipMarker);
        ownshipMarker.current = new maplibregl.Marker({
          element: el,
          rotationAlignment: 'map',
        })
          .setLngLat([fix.lon, fix.lat])
          .addTo(map);
      } else {
        ownshipMarker.current.setLngLat([fix.lon, fix.lat]);
      }
      // F6 (2026-05-16 perf audit): setRotation only — manual
      // style.transform would fight MapLibre's internal compose.
      ownshipMarker.current.setRotation(fix.headingDeg ?? 0);
    };

    renderMarker(useGpsStore.getState());
    const unsub = useGpsStore.subscribe(renderMarker);
    return () => unsub();
  }, [showOwnship, activeOwnshipMarker]);

  // When the marker style changes while GPS fix is unchanged, force
  // the SVG innerHTML to update (the effect above only redraws on
  // state changes, not prop changes to activeOwnshipMarker alone).
  useEffect(() => {
    if (!ownshipMarker.current) return;
    const el = ownshipMarker.current.getElement();
    const currentTransform = el.style.transform;
    el.innerHTML = ownshipMarkerSvg(activeOwnshipMarker);
    el.style.transform = currentTransform;
  }, [activeOwnshipMarker]);

  // Follow-ownship pan — easeTo on each GPS fix when follow mode is on.
  // Uses a local `programmingMove` flag so our own easeTo doesn't falsely
  // disengage follow mode, and exposes that flag as __fyProgrammingMove
  // on the map instance so the moveend handler (in MapView) can skip
  // the bbox-refresh cascade during programmatic pans.
  useEffect(() => {
    if (!followOwnship) return;
    const map = getMapInstance();
    if (!map) return;

    let programmingMove = false;
    const mapWithFlag = map as unknown as { __fyProgrammingMove?: boolean };

    const unsub = useGpsStore.subscribe((state) => {
      const fix = state.fix;
      if (!fix) return;
      if (!useUiStore.getState().followOwnship) return;
      programmingMove = true;
      mapWithFlag.__fyProgrammingMove = true;
      map.easeTo({ center: [fix.lon, fix.lat], duration: 500 });
      setTimeout(() => {
        programmingMove = false;
        mapWithFlag.__fyProgrammingMove = false;
      }, 600);
    });

    function onUserMove(e: { originalEvent?: Event }) {
      if (!e.originalEvent) return;
      if (programmingMove) return;
      useUiStore.getState().setFollowOwnship(false);
    }
    map.on('dragstart', onUserMove);
    map.on('zoomstart', onUserMove);

    return () => {
      unsub();
      map.off('dragstart', onUserMove);
      map.off('zoomstart', onUserMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followOwnship]);

  return null;
}
